import React, { useEffect, useMemo, useState } from "react";
import {
  opsGetPolicyConsole,
  opsSavePolicyPage,
} from "../../../api/adminOpsApi";

const AUDIENCES = [
  { key: "user", label: "User App", title: "Policies" },
  { key: "owner", label: "Owner App", title: "Owner Policies" },
  { key: "driver", label: "Driver App", title: "Driver Policies" },
  { key: "vendor", label: "Vendor App", title: "Vendor Policies" },
];

const INDIAN_STATES = [
  { code: "AN", label: "Andaman and Nicobar Islands" },
  { code: "AP", label: "Andhra Pradesh" },
  { code: "AR", label: "Arunachal Pradesh" },
  { code: "AS", label: "Assam" },
  { code: "BR", label: "Bihar" },
  { code: "CH", label: "Chandigarh" },
  { code: "CG", label: "Chhattisgarh" },
  { code: "DL", label: "Delhi" },
  { code: "GA", label: "Goa" },
  { code: "GJ", label: "Gujarat" },
  { code: "HR", label: "Haryana" },
  { code: "HP", label: "Himachal Pradesh" },
  { code: "JH", label: "Jharkhand" },
  { code: "KA", label: "Karnataka" },
  { code: "KL", label: "Kerala" },
  { code: "MP", label: "Madhya Pradesh" },
  { code: "MH", label: "Maharashtra" },
  { code: "MN", label: "Manipur" },
  { code: "ML", label: "Meghalaya" },
  { code: "MZ", label: "Mizoram" },
  { code: "NL", label: "Nagaland" },
  { code: "OD", label: "Odisha" },
  { code: "PB", label: "Punjab" },
  { code: "RJ", label: "Rajasthan" },
  { code: "SK", label: "Sikkim" },
  { code: "TN", label: "Tamil Nadu" },
  { code: "TS", label: "Telangana" },
  { code: "TR", label: "Tripura" },
  { code: "UP", label: "Uttar Pradesh" },
  { code: "UK", label: "Uttarakhand" },
  { code: "WB", label: "West Bengal" },
];

function cls(...xs) {
  return xs.filter(Boolean).join(" ");
}

function emptyPage(audience) {
  const meta = AUDIENCES.find((item) => item.key === audience) || AUDIENCES[0];
  return {
    audience,
    menuLabel: "Policies",
    title: meta.title,
    subtitle: `Policies added by admin for ${meta.label.toLowerCase()} will appear here.`,
    lastUpdated: null,
    sections: [],
  };
}

function normalizePage(audience, raw) {
  const page = { ...emptyPage(audience), ...(raw || {}) };
  page.audience = audience;
  page.menuLabel = String(page.menuLabel || "Policies");
  page.title = String(page.title || emptyPage(audience).title);
  page.subtitle = String(page.subtitle || emptyPage(audience).subtitle);
  page.sections = Array.isArray(page.sections)
    ? page.sections.map((section, index) => ({
        key: String(section?.key || `${audience}-policy-${index + 1}`),
        title: String(section?.title || ""),
        content: String(section?.content || ""),
        visibilityScope: String(section?.visibilityScope || "PAN_INDIA").toUpperCase() === "STATES" ? "STATES" : "PAN_INDIA",
        states: Array.isArray(section?.states)
          ? section.states.map((item) => String(item || "").trim().toUpperCase()).filter(Boolean)
          : [],
      }))
    : [];
  return page;
}

function makeSection() {
  return {
    key: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title: "",
    content: "",
    visibilityScope: "PAN_INDIA",
    states: [],
  };
}

const ALLOWED_TAGS = new Set([
  "P",
  "BR",
  "STRONG",
  "B",
  "EM",
  "I",
  "U",
  "UL",
  "OL",
  "LI",
  "H1",
  "H2",
  "H3",
  "BLOCKQUOTE",
]);

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function looksLikeHtml(value) {
  return /<\/?[a-z][\s\S]*>/i.test(String(value || ""));
}

function plainTextToHtml(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  return text
    .split(/\n{2,}/)
    .map((block) => `<p>${escapeHtml(block).replace(/\n/g, "<br>")}</p>`)
    .join("");
}

function sanitizeRichHtml(rawHtml) {
  if (!rawHtml || typeof window === "undefined") return "";
  const parser = new window.DOMParser();
  const doc = parser.parseFromString(`<div>${rawHtml}</div>`, "text/html");
  const container = doc.body.firstElementChild;
  if (!container) return "";

  const sanitizeNode = (node) => {
    if (node.nodeType === window.Node.TEXT_NODE) {
      return doc.createTextNode(node.textContent || "");
    }
    if (node.nodeType !== window.Node.ELEMENT_NODE) {
      return null;
    }

    const tag = node.tagName.toUpperCase();
    if (!ALLOWED_TAGS.has(tag)) {
      const fragment = doc.createDocumentFragment();
      Array.from(node.childNodes).forEach((child) => {
        const cleanChild = sanitizeNode(child);
        if (cleanChild) fragment.appendChild(cleanChild);
      });
      return fragment;
    }

    const clean = doc.createElement(tag.toLowerCase());
    Array.from(node.childNodes).forEach((child) => {
      const cleanChild = sanitizeNode(child);
      if (cleanChild) clean.appendChild(cleanChild);
    });
    return clean;
  };

  const output = doc.createElement("div");
  Array.from(container.childNodes).forEach((child) => {
    const cleanChild = sanitizeNode(child);
    if (cleanChild) output.appendChild(cleanChild);
  });

  return output.innerHTML
    .replace(/<p><\/p>/g, "")
    .replace(/(<br>\s*){3,}/g, "<br><br>")
    .trim();
}

function insertHtmlAtCursor(html) {
  if (!html || typeof document === "undefined") return;
  document.execCommand("insertHTML", false, html);
}

function toggleStateCode(list, stateCode) {
  const current = Array.isArray(list) ? list : [];
  return current.includes(stateCode)
    ? current.filter((item) => item !== stateCode)
    : [...current, stateCode];
}

function RichTextField({ value, onChange, placeholder }) {
  const editorRef = React.useRef(null);
  const isHtml = looksLikeHtml(value);
  const displayHtml = isHtml ? value : plainTextToHtml(value);

  React.useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (el.innerHTML !== displayHtml) {
      el.innerHTML = displayHtml;
    }
  }, [displayHtml]);

  function syncFromEditor() {
    const el = editorRef.current;
    if (!el) return;
    onChange(sanitizeRichHtml(el.innerHTML));
  }

  function applyCommand(command, commandValue = null) {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    document.execCommand(command, false, commandValue);
    syncFromEditor();
  }

  return (
    <div className="mt-2 rounded-2xl border border-slate-300 bg-white">
      <div className="flex flex-wrap gap-2 border-b border-slate-200 px-3 py-2">
        {[
          { label: "B", action: () => applyCommand("bold"), className: "font-black" },
          { label: "I", action: () => applyCommand("italic"), className: "italic" },
          { label: "U", action: () => applyCommand("underline"), className: "underline" },
          { label: "Bullets", action: () => applyCommand("insertUnorderedList") },
          { label: "1. List", action: () => applyCommand("insertOrderedList") },
          { label: "H2", action: () => applyCommand("formatBlock", "h2") },
          { label: "Quote", action: () => applyCommand("formatBlock", "blockquote") },
          { label: "Clear", action: () => applyCommand("removeFormat") },
        ].map((item) => (
          <button
            key={item.label}
            type="button"
            onClick={item.action}
            className={cls(
              "inline-flex h-8 items-center rounded-lg border border-slate-200 px-3 text-xs font-bold text-slate-700 hover:bg-slate-50",
              item.className
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={syncFromEditor}
        onBlur={syncFromEditor}
        onPaste={(e) => {
          e.preventDefault();
          const html = e.clipboardData.getData("text/html");
          const text = e.clipboardData.getData("text/plain");
          const payload = html ? sanitizeRichHtml(html) : plainTextToHtml(text);
          insertHtmlAtCursor(payload || escapeHtml(text).replace(/\n/g, "<br>"));
          syncFromEditor();
        }}
        data-placeholder={placeholder}
        className="min-h-[180px] w-full px-3 py-3 text-sm leading-6 text-slate-900 outline-none empty:before:pointer-events-none empty:before:text-slate-400 empty:before:content-[attr(data-placeholder)] [&_blockquote]:border-l-4 [&_blockquote]:border-slate-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_h1]:text-xl [&_h1]:font-black [&_h2]:text-lg [&_h2]:font-black [&_h3]:text-base [&_h3]:font-black [&_li]:ml-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5"
      />
    </div>
  );
}

export default function OpsPolicies() {
  const [selectedAudience, setSelectedAudience] = useState("user");
  const [pages, setPages] = useState(() =>
    AUDIENCES.reduce((acc, item) => {
      acc[item.key] = emptyPage(item.key);
      return acc;
    }, {})
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const data = await opsGetPolicyConsole();
        if (!active) return;
        setPages(
          AUDIENCES.reduce((acc, item) => {
            acc[item.key] = normalizePage(item.key, data?.[item.key]);
            return acc;
          }, {})
        );
      } catch (e) {
        if (active) setError(e?.message || "Failed to load policy console.");
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, []);

  const page = useMemo(
    () => normalizePage(selectedAudience, pages[selectedAudience]),
    [pages, selectedAudience]
  );

  function patchPage(next) {
    setPages((current) => ({
      ...current,
      [selectedAudience]: normalizePage(selectedAudience, next),
    }));
  }

  function patchSection(key, field, value) {
    patchPage({
      ...page,
      sections: page.sections.map((section) =>
        section.key === key ? { ...section, [field]: value } : section
      ),
    });
  }

  function addSection() {
    patchPage({
      ...page,
      sections: [...page.sections, makeSection()],
    });
  }

  function removeSection(key) {
    patchPage({
      ...page,
      sections: page.sections.filter((section) => section.key !== key),
    });
  }

  async function savePage() {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const saved = await opsSavePolicyPage(selectedAudience, page);
      setPages((current) => ({
        ...current,
        [selectedAudience]: normalizePage(selectedAudience, saved),
      }));
      setNotice(`Saved ${selectedAudience} policies.`);
    } catch (e) {
      setError(e?.message || "Failed to save policy page.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-5 py-4">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
            Policies Console
          </div>
          <div className="mt-1 text-2xl font-black text-slate-900">
            App Policy Manager
          </div>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
            Manage policy headers and full content for the user, owner, driver,
            and vendor apps. Each row becomes one collapsible policy item inside
            the app policy screen. Platform fee policy is not edited here.
          </p>
        </div>

        <div className="border-b border-slate-200 px-5 py-4">
          <div className="flex flex-wrap gap-2">
            {AUDIENCES.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => {
                  setSelectedAudience(item.key);
                  setNotice("");
                  setError("");
                }}
                className={cls(
                  "inline-flex h-11 items-center rounded-xl border px-4 text-sm font-bold transition",
                  selectedAudience === item.key
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                )}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-4 px-5 py-5 xl:grid-cols-[minmax(0,1.2fr)_360px]">
          <div className="space-y-4">
            {loading ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-8 text-sm font-semibold text-slate-500">
                Loading policy editor...
              </div>
            ) : null}

            {!loading ? (
              <>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="block">
                      <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                        Menu Label
                      </div>
                      <input
                        value={page.menuLabel}
                        onChange={(e) =>
                          patchPage({ ...page, menuLabel: e.target.value })
                        }
                        className="mt-2 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-900 outline-none focus:border-slate-900"
                        placeholder="Policies"
                      />
                    </label>

                    <label className="block">
                      <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                        Page Title
                      </div>
                      <input
                        value={page.title}
                        onChange={(e) =>
                          patchPage({ ...page, title: e.target.value })
                        }
                        className="mt-2 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-900 outline-none focus:border-slate-900"
                        placeholder="Policies"
                      />
                    </label>
                  </div>

                  <label className="mt-4 block">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Page Subtitle
                    </div>
                    <textarea
                      value={page.subtitle}
                      onChange={(e) =>
                        patchPage({ ...page, subtitle: e.target.value })
                      }
                      rows={3}
                      className="mt-2 w-full rounded-2xl border border-slate-300 bg-white px-3 py-3 text-sm leading-6 text-slate-900 outline-none focus:border-slate-900"
                      placeholder="Short intro shown above the policy accordion."
                    />
                  </label>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-lg font-black text-slate-900">
                      Policy Sections
                    </div>
                    <div className="text-sm text-slate-500">
                      Add, edit, or delete the collapsible policy items for this
                      audience.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={addSection}
                    className="inline-flex h-11 items-center rounded-xl border border-slate-900 bg-slate-900 px-4 text-sm font-bold text-white"
                  >
                    Add Policy
                  </button>
                </div>

                {page.sections.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-8 text-sm font-semibold text-slate-500">
                    No policy items yet for this audience.
                  </div>
                ) : null}

                {page.sections.map((section, index) => (
                  <div
                    key={section.key}
                    className="rounded-2xl border border-slate-200 bg-white p-4"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-black text-slate-900">
                        Policy {index + 1}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeSection(section.key)}
                        className="inline-flex h-9 items-center rounded-lg border border-rose-200 bg-rose-50 px-3 text-xs font-bold text-rose-700 hover:bg-rose-100"
                      >
                        Delete
                      </button>
                    </div>

                    <label className="mt-4 block">
                      <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                        Header
                      </div>
                      <input
                        value={section.title}
                        onChange={(e) =>
                          patchSection(section.key, "title", e.target.value)
                        }
                        className="mt-2 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-900 outline-none focus:border-slate-900"
                        placeholder="Cancellation Policy"
                      />
                    </label>

                    <label className="mt-4 block">
                      <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                        Content
                      </div>
                      <RichTextField
                        value={section.content}
                        onChange={(nextValue) =>
                          patchSection(section.key, "content", nextValue)
                        }
                        placeholder="Full policy text shown when the header is opened in the app."
                      />
                    </label>

                    <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                        Location Visibility
                      </div>
                      <div className="mt-3 inline-flex rounded-2xl border border-slate-200 bg-white p-1">
                        {[
                          { key: "PAN_INDIA", label: "Pan India" },
                          { key: "STATES", label: "Selected States" },
                        ].map((item) => {
                          const active = (section.visibilityScope || "PAN_INDIA") === item.key;
                          return (
                            <button
                              key={item.key}
                              type="button"
                              onClick={() =>
                                patchPage({
                                  ...page,
                                  sections: page.sections.map((currentSection) =>
                                    currentSection.key === section.key
                                      ? {
                                          ...currentSection,
                                          visibilityScope: item.key,
                                          states: item.key === "PAN_INDIA" ? [] : currentSection.states || [],
                                        }
                                      : currentSection
                                  ),
                                })
                              }
                              className={cls(
                                "rounded-xl px-4 py-2 text-sm font-bold transition",
                                active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                              )}
                            >
                              {item.label}
                            </button>
                          );
                        })}
                      </div>

                      {(section.visibilityScope || "PAN_INDIA") === "STATES" ? (
                        <div className="mt-4">
                          <div className="text-xs font-semibold text-slate-500">
                            Show this policy item only inside the selected states.
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {INDIAN_STATES.map((state) => {
                              const active = (section.states || []).includes(state.code);
                              return (
                                <button
                                  key={state.code}
                                  type="button"
                                  onClick={() =>
                                    patchSection(section.key, "states", toggleStateCode(section.states, state.code))
                                  }
                                  className={cls(
                                    "inline-flex items-center rounded-full border px-3 py-2 text-xs font-bold transition",
                                    active
                                      ? "border-slate-900 bg-slate-900 text-white"
                                      : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"
                                  )}
                                >
                                  {state.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3 text-sm text-slate-600">
                          This policy item will appear for all states.
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </>
            ) : null}
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Audience Summary
              </div>
              <div className="mt-2 text-2xl font-black text-slate-900">
                {AUDIENCES.find((item) => item.key === selectedAudience)?.label}
              </div>
              <div className="mt-1 text-sm text-slate-600">
                Last updated: {page.lastUpdated || "Not saved yet"}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="rounded-2xl border border-slate-200 bg-white p-3">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Menu
                  </div>
                  <div className="mt-2 text-sm font-black text-slate-900">
                    {page.menuLabel || "Policies"}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-3">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Items
                  </div>
                  <div className="mt-2 text-sm font-black text-slate-900">
                    {page.sections.length}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                App Behavior
              </div>
              <div className="mt-2 text-sm leading-6 text-slate-600">
                Each header becomes a collapsed row on the app policy page.
                Tapping the header opens the full content. Empty policy lists
                show a blank-state message instead of legacy static copy.
              </div>
            </div>

            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
                {error}
              </div>
            ) : null}

            {notice ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
                {notice}
              </div>
            ) : null}

            <button
              type="button"
              onClick={savePage}
              disabled={loading || saving}
              className={cls(
                "inline-flex h-12 w-full items-center justify-center rounded-xl text-sm font-bold transition",
                loading || saving
                  ? "cursor-not-allowed bg-slate-300 text-white"
                  : "bg-slate-900 text-white hover:bg-slate-800"
              )}
            >
              {saving ? "Saving..." : "Save Policies"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
